"use client";

import { useTransition, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useRouter } from "next/navigation";
import { trackOpenAiAdsConversion } from "@drpcrd/shared/openai-ads";
import { submitContactForm } from "./actions";
import {
  Button,
  Checkbox,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  InputTextArea,
  InputTextField,
  InstagramLogo,
  TikTokLogo,
  TwitchLogo,
  Typography,
  YouTubeLogo,
  SnapchatLogo,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@drpcrd/shared/ui";

const ROLE_OPTIONS = [
  { value: "brand", label: "I work for a brand" },
  { value: "creator", label: "I'm a creator" },
  { value: "talent_agent", label: "I'm a talent agent" },
  { value: "journalist", label: "I'm a journalist" },
  { value: "interested", label: "I'm just interested" },
] as const;

const SOCIAL_OPTIONS = [
  {
    name: "instagramHandle" as const,
    placeholder: "Instagram handle",
    icon: <InstagramLogo className="h-2 w-2 shrink-0" />,
  },
  {
    name: "tiktokHandle" as const,
    placeholder: "TikTok handle",
    icon: <TikTokLogo className="h-2 w-2 shrink-0" />,
  },
  {
    name: "youtubeChannel" as const,
    placeholder: "YouTube channel",
    icon: <YouTubeLogo className="h-2 w-2 shrink-0" />,
  },
  {
    name: "twitchChannel" as const,
    placeholder: "Twitch channel",
    icon: <TwitchLogo className="h-2 w-2 shrink-0" />,
  },
  {
    name: "snapHandle" as const,
    placeholder: "Snap handle",
    icon: <SnapchatLogo className="h-2 w-2 shrink-0" />,
  },
];

const schema = z
  .object({
    website: z.string().optional(),
    firstName: z.string().trim().min(1, { message: "First name is required" }),
    lastName: z.string().trim().optional(),
    email: z.email({ message: "Please enter a valid email address" }),
    company: z.string().trim().optional(),
    enquiryType: z.enum(
      ["brand", "creator", "talent_agent", "journalist", "interested"],
      {
        error: "Please select what best describes you",
      },
    ),
    instagramHandle: z.string().trim().optional(),
    tiktokHandle: z.string().trim().optional(),
    youtubeChannel: z.string().trim().optional(),
    twitchChannel: z.string().trim().optional(),
    snapHandle: z.string().trim().optional(),
    message: z.string().trim().optional(),
    newsletterOptIn: z.boolean().optional(),
    applyForCreatorAccount: z.boolean().optional(),
  })
  .superRefine((data, ctx) => {
    if (data.enquiryType === "brand" && !data.company?.trim()) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Brand name is required",
        path: ["company"],
      });
    }
    if (data.enquiryType === "talent_agent" && !data.company?.trim()) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Talent agency name is required",
        path: ["company"],
      });
    }
    if (data.enquiryType === "creator") {
      const hasSocial =
        data.instagramHandle ||
        data.tiktokHandle ||
        data.youtubeChannel ||
        data.twitchChannel ||
        data.snapHandle;
      if (!hasSocial) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Please add at least one social media handle",
          path: ["instagramHandle"],
        });
      }
    }
  });

type ContactFormValues = z.infer<typeof schema>;

/**
 * Nutcake-branded contact form that submits an enquiry to the internal team.
 * On success, the form is replaced with a confirmation message.
 * On error, a top-level banner is shown and field values are preserved.
 */
export const ContactForm = () => {
  const [isPending, startTransition] = useTransition();
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const router = useRouter();

  const form = useForm<ContactFormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      website: "",
      firstName: "",
      lastName: "",
      email: "",
      company: "",
      enquiryType: undefined,
      instagramHandle: "",
      tiktokHandle: "",
      youtubeChannel: "",
      twitchChannel: "",
      snapHandle: "",
      message: "",
      newsletterOptIn: false,
      applyForCreatorAccount: false,
    },
    mode: "onSubmit",
  });

  const enquiryType = form.watch("enquiryType");
  const isCreator = enquiryType === "creator";
  const isBrand = enquiryType === "brand";
  const isTalentAgent = enquiryType === "talent_agent";

  const onEnquiryTypeChange = (value: string) => {
    form.reset({
      website: "",
      enquiryType: value as ContactFormValues["enquiryType"],
      firstName: "",
      lastName: "",
      email: "",
      company: "",
      instagramHandle: "",
      tiktokHandle: "",
      youtubeChannel: "",
      twitchChannel: "",
      snapHandle: "",
      message: "",
      newsletterOptIn: false,
      applyForCreatorAccount: false,
    });
  };

  const onSubmit = (values: ContactFormValues) => {
    setErrorMessage(null);
    startTransition(async () => {
      const result = await submitContactForm(values);
      if (result.success) {
        trackOpenAiAdsConversion("lead_created", { type: "customer_action" });
        router.push("/contact/thank-you");
      } else {
        setErrorMessage(
          result.data ??
            "Something went wrong. Please try again or email us directly.",
        );
      }
    });
  };

  return (
    <Form {...form}>
      <form
        className="flex flex-col gap-4 sm:gap-6"
        onSubmit={form.handleSubmit(onSubmit)}
        noValidate
      >
        {errorMessage && (
          <div className="rounded-lg border border-pink-500 bg-pink-50 px-4 py-3">
            <Typography size="sm" color="black">
              {errorMessage}
            </Typography>
          </div>
        )}

        <FormField
          control={form.control}
          name="enquiryType"
          render={({ field }) => (
            <FormItem className="-mb-1.5 space-y-1.5">
              <FormLabel className="flex w-full flex-row gap-1 font-sans text-sm font-semibold">
                What best describes you?
              </FormLabel>
              <Select onValueChange={onEnquiryTypeChange} value={field.value}>
                <FormControl>
                  <SelectTrigger>
                    <SelectValue placeholder="Select an option" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {ROLE_OPTIONS.map(({ value, label }) => (
                    <SelectItem className="text-lg" key={value} value={value}>
                      {label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 sm:gap-2">
          <InputTextField
            control={form.control}
            name="firstName"
            label="First name"
            placeholder="Your first name"
            autoComplete="given-name"
            maxLength={50}
            required
          />
          <InputTextField
            control={form.control}
            name="lastName"
            label="Last name"
            placeholder="Your last name"
            autoComplete="family-name"
            maxLength={50}
          />
        </div>

        {isBrand && (
          <InputTextField
            control={form.control}
            name="company"
            label="Brand name"
            placeholder="Your brand"
            autoComplete="organization"
            maxLength={100}
          />
        )}

        {isTalentAgent && (
          <InputTextField
            control={form.control}
            name="company"
            label="Talent agency name"
            placeholder="Your talent agency name"
            autoComplete="organization"
            maxLength={100}
          />
        )}

        <InputTextField
          control={form.control}
          name="email"
          label="Email"
          type="email"
          placeholder="Your email address"
          autoComplete="email"
          required
        />

        {isCreator && (
          <div className="flex flex-col gap-2">
            <Typography size="sm" weight="semibold">
              Your socials
            </Typography>
            {form.formState.errors.instagramHandle?.message &&
              !form.getValues("instagramHandle") && (
                <Typography size="sm" className="text-pink-500">
                  {form.formState.errors.instagramHandle.message}
                </Typography>
              )}
            {SOCIAL_OPTIONS.map(({ name, placeholder, icon }) => (
              <FormField
                key={name}
                control={form.control}
                name={name}
                render={({ field }) => (
                  <FormItem className="space-y-0">
                    <FormControl>
                      <div className="gap- flex w-full items-center overflow-hidden rounded-lg border border-neutral-500 bg-white p-1.5 focus-within:outline-2 focus-within:outline-black">
                        <div className="flex items-center pr-1.5">{icon}</div>
                        <div className="w-px self-stretch bg-neutral-500" />
                        <input
                          {...field}
                          placeholder={placeholder}
                          className="autofill-white w-full flex-1 border-0 bg-transparent pl-2 font-sans text-lg leading-2.75 font-normal outline-none placeholder:text-neutral-900"
                        />
                      </div>
                    </FormControl>
                  </FormItem>
                )}
              />
            ))}
          </div>
        )}

        <InputTextArea
          control={form.control}
          name="message"
          label="Your message"
          placeholder="Type anything"
          rows={4}
          maxLength={2000}
        />

        {isCreator && (
          <FormField
            control={form.control}
            name="applyForCreatorAccount"
            render={({ field }) => (
              <FormItem className="bg-primary-50 border-primary-100 flex cursor-pointer flex-row items-center gap-1.5 space-y-0 rounded-lg border px-2 py-3">
                <FormControl>
                  <Checkbox
                    checked={field.value}
                    onCheckedChange={field.onChange}
                    className="border-primary-700 data-[state=checked]:bg-primary-700 size-2.5 cursor-pointer bg-white data-[state=checked]:text-white [&_svg]:size-2"
                  />
                </FormControl>
                <FormLabel className="text-primary-700 cursor-pointer font-sans text-sm font-semibold">
                  I want to apply to be a Nutcake creator
                </FormLabel>
              </FormItem>
            )}
          />
        )}

        {!isCreator && (
          <FormField
            control={form.control}
            name="newsletterOptIn"
            render={({ field }) => (
              <FormItem className="bg-primary-50 border-primary-100 flex cursor-pointer flex-row items-center gap-1.5 space-y-0 rounded-lg border px-2 py-3">
                <FormControl>
                  <Checkbox
                    checked={field.value}
                    onCheckedChange={field.onChange}
                    className="border-primary-700 data-[state=checked]:bg-primary-700 size-2.5 cursor-pointer bg-white data-[state=checked]:text-white [&_svg]:size-2"
                  />
                </FormControl>
                <FormLabel className="text-primary-700 cursor-pointer font-sans text-sm font-semibold">
                  Sign me up to the Nutcake newsletter
                </FormLabel>
              </FormItem>
            )}
          />
        )}

        <div className="flex justify-end">
          {/* Honeypot: hidden from real users, bots fill it and get silently rejected */}
          <input
            {...form.register("website")}
            type="text"
            name="website"
            tabIndex={-1}
            aria-hidden="true"
            autoComplete="off"
            className="absolute -left-[9999px] h-0 w-0 overflow-hidden opacity-0"
          />
          <Button
            type="submit"
            variant="filled"
            loading={isPending}
            disabled={isPending}
          >
            {isPending ? "Sending…" : "Send"}
          </Button>
        </div>
      </form>
    </Form>
  );
};
